import { readCheckoutReturn } from "@/lib/billing-session";

export const ORGANIZATION_TABS = ["members", "billing", "general", "activity"] as const;
export type OrganizationTab = (typeof ORGANIZATION_TABS)[number];

export const ORGANIZATION_TAB_LABELS: Record<OrganizationTab, string> = {
  members: "Members",
  billing: "Billing",
  general: "General",
  activity: "Activity",
};

/**
 * `/organization` search params. Stripe's Checkout return parameters pass through untouched so
 * the checkout notice can confirm (and on a transient failure, retry) from the URL.
 */
export type OrganizationSearch = { tab?: OrganizationTab; checkout?: string; session_id?: string };

export function parseOrganizationSearch(search: Record<string, unknown>): OrganizationSearch {
  const result: OrganizationSearch = {};
  if (ORGANIZATION_TABS.includes(search.tab as OrganizationTab)) result.tab = search.tab as OrganizationTab;
  if (typeof search.checkout === "string") result.checkout = search.checkout;
  if (typeof search.session_id === "string") result.session_id = search.session_id;
  return result;
}

/** Owners and admins see every tab; everyone else sees only what they can use. */
export function visibleOrganizationTabs(canManageOrg: boolean): OrganizationTab[] {
  return canManageOrg ? [...ORGANIZATION_TABS] : ["general", "billing"];
}

/**
 * The selected tab: the requested one if visible, Billing for a Checkout return, else the first.
 * `isCheckoutReturn` is captured when the page loads, because the checkout notice clears the
 * return parameters from the URL once it confirms.
 */
export function resolveOrganizationTab(
  search: OrganizationSearch,
  canManageOrg: boolean,
  isCheckoutReturn: boolean
): OrganizationTab {
  const visible = visibleOrganizationTabs(canManageOrg);
  if (search.tab && visible.includes(search.tab)) return search.tab;
  if (isCheckoutReturn) return "billing";
  return visible[0];
}

/** Checkout return params still in the address bar; the notice clears them once it confirms. */
export function liveCheckoutSearch(): Pick<OrganizationSearch, "checkout" | "session_id"> {
  const checkoutReturn = readCheckoutReturn(window.location.search);
  if (!checkoutReturn) return {};
  return checkoutReturn.kind === "success"
    ? { checkout: "success", session_id: checkoutReturn.sessionId }
    : { checkout: "canceled" };
}
